/**
 * Service for managing Unreal Engine project URLs from AWS Secrets Manager
 * This service fetches the UnrealProjects secret and provides mapping of project IDs to external URLs
 */

import axios from 'axios';
import { baseApi } from '../API/api';

class UnrealProjectsService {
  constructor() {
    this.secretCache = null;
    this.cacheTimestamp = null;
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes cache
  }

  /**
   * Fetch the UnrealProjects secret from AWS Secrets Manager
   * @returns {Promise<Object>} Object with project IDs as keys and URLs as values
   */
  async fetchUnrealProjectsSecret() {
    try {
      console.log('🔐 Fetching UnrealProjects secret from AWS Secrets Manager...');
      console.log('🔐 Using baseApi:', baseApi);
      const url = `${baseApi}/api/secrets/unreal-projects`;
      console.log('🔐 Full URL:', url);
      
      const response = await axios.get(url, {
        timeout: 10000 // 10 second timeout
      });

      console.log('🔐 API Response:', response.data);
      if (response.data && response.data.success) {
        console.log('🔐 Successfully fetched UnrealProjects secret:', response.data.secret);
        return response.data.secret;
      } else {
        console.warn('🔐 Failed to fetch UnrealProjects secret:', response.data?.error);
        return {};
      }
    } catch (error) {
      console.error('🔐 Error fetching UnrealProjects secret:', error);
      console.error('🔐 Error details:', error.response?.data || error.message);
      return {};
    }
  }

  /**
   * Get the cached secret or fetch it if cache is expired
   * @returns {Promise<Object>} Secret data
   */
  async getSecretData() {
    const now = Date.now();
    
    // Return cached data if still valid
    if (this.secretCache && this.cacheTimestamp && (now - this.cacheTimestamp) < this.cacheExpiry) {
      console.log('🔐 Using cached UnrealProjects secret');
      return this.secretCache;
    }

    // Fetch fresh data
    console.log('🔐 Cache expired or missing, fetching fresh UnrealProjects secret');
    this.secretCache = await this.fetchUnrealProjectsSecret();
    this.cacheTimestamp = now;
    
    // Debug: Check if we got valid data
    console.log('🔐 Cache update result:', {
      secretCache: this.secretCache,
      secretCacheType: typeof this.secretCache,
      secretCacheKeys: this.secretCache ? Object.keys(this.secretCache) : 'No keys'
    });
    
    return this.secretCache;
  }

  /**
   * Check if a project ID has an external Unreal Engine URL
   * @param {number|string} projectId - The project ID to check
   * @returns {Promise<string|null>} The external URL if found, null otherwise
   */
  async getProjectExternalUrl(projectId) {
    try {
      console.log(`🔐 Getting external URL for project ${projectId} (type: ${typeof projectId})`);
      const secretData = await this.getSecretData();
      console.log(`🔐 Secret data received:`, secretData);
      const projectIdStr = String(projectId);
      console.log(`🔐 Looking for project ID string: "${projectIdStr}"`);
      
      if (secretData && secretData[projectIdStr]) {
        console.log(`🔐 Found external URL for project ${projectId}:`, secretData[projectIdStr]);
        return secretData[projectIdStr];
      }
      
      console.log(`🔐 No external URL found for project ${projectId}. Available keys:`, secretData ? Object.keys(secretData) : 'No secret data');
      return null;
    } catch (error) {
      console.error('🔐 Error getting project external URL:', error);
      return null;
    }
  }

  /**
   * Check if a project should open in external URL instead of internal 3D view
   * @param {number|string} projectId - The project ID to check
   * @returns {Promise<boolean>} True if project has external URL, false otherwise
   */
  async hasExternalUrl(projectId) {
    const url = await this.getProjectExternalUrl(projectId);
    return url !== null && url.trim() !== '';
  }

  /**
   * Clear the cache (useful for testing or when secret is updated)
   */
  clearCache() {
    console.log('🔐 Clearing UnrealProjects secret cache');
    this.secretCache = null;
    this.cacheTimestamp = null;
  }

  /**
   * Force refresh the secret data (clear cache and fetch fresh)
   */
  async forceRefresh() {
    console.log('🔐 Force refreshing UnrealProjects secret...');
    this.clearCache();
    return await this.getSecretData();
  }
}

// Export singleton instance
export default new UnrealProjectsService();
